// frontend/src/modules/empresas/pages/ConfigurarEmpresa.js
import React, { useState, useEffect } from 'react';
import { FaSave, FaUpload, FaTrash, FaBuilding, FaPhone, FaEnvelope, FaMapMarkerAlt, FaGlobe } from 'react-icons/fa';
import Logo from '../../../components/Logo';

function ConfigurarEmpresa({ usuario, isDark = false }) {
  const empresaId = usuario?.empresa_id || 1;
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [empresa, setEmpresa] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    nome_empresa: '',
    nome_fantasia: '',
    telefone: '',
    email: '',
    site: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    pais: 'BRASIL'
  });

  // Buscar dados da empresa
  useEffect(() => {
    const fetchEmpresa = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/empresas/${empresaId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Erro ao buscar dados da empresa');
        const data = await response.json();
        setEmpresa(data);
        setFormData({
          nome_empresa: data.nome_empresa || '',
          nome_fantasia: data.nome_fantasia || '',
          telefone: data.telefone || '',
          email: data.email || '',
          site: data.site || '',
          cep: data.cep || '',
          logradouro: data.logradouro || '',
          numero: data.numero || '',
          complemento: data.complemento || '',
          bairro: data.bairro || '',
          cidade: data.cidade || '',
          estado: data.estado || '',
          pais: data.pais || 'BRASIL'
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEmpresa();
  }, [empresaId, token]);

  // Handle change dos inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle upload do logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      setError('Formato de imagem não suportado. Use JPG, PNG, GIF ou WEBP.');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('A imagem não pode ter mais que 5MB.');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  // Remover logo
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    // Resetar o input file
    document.getElementById('logoInput').value = '';
  };

  // Salvar dados da empresa
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // 1. Salvar dados da empresa
      const response = await fetch(`/api/empresas/${empresaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nome_empresa: formData.nome_empresa,
          nome_fantasia: formData.nome_fantasia,
          telefone: formData.telefone,
          email: formData.email,
          site: formData.site,
          cep: formData.cep,
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado,
          pais: formData.pais
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao salvar dados');
      }

      // 2. Se tiver logo, fazer upload
      if (logoFile) {
        const formDataLogo = new FormData();
        formDataLogo.append('logo', logoFile);

        const logoResponse = await fetch(`/api/empresas/${empresaId}/logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataLogo
        });

        if (!logoResponse.ok) {
          const err = await logoResponse.json();
          throw new Error(err.error || 'Erro ao fazer upload do logo');
        }
      }

      setSuccess('✅ Dados da empresa atualizados com sucesso!');
      
      // Recarregar dados
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const colors = {
    background: isDark ? '#121212' : '#f8f9fa',
    card: isDark ? '#1e1e1e' : '#ffffff',
    text: isDark ? '#e9ecef' : '#212529',
    border: isDark ? '#333333' : '#dee2e6',
    inputBg: isDark ? '#2d2d2d' : '#ffffff',
    inputBorder: isDark ? '#444444' : '#ced4da',
    inputText: isDark ? '#e9ecef' : '#212529',
    label: isDark ? '#e9ecef' : '#212529'
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: colors.text }}>
        🔄 Carregando dados da empresa...
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: colors.background,
      minHeight: '100vh'
    }}>
      <div style={{
        backgroundColor: colors.card,
        borderRadius: '8px',
        padding: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: isDark ? `1px solid ${colors.border}` : 'none'
      }}>
        <h2 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px',
          color: colors.text,
          marginBottom: '5px'
        }}>
          <FaBuilding /> Configurações da Empresa
        </h2>
        <p style={{ color: '#6c757d', marginBottom: '25px' }}>
          Atualize os dados de contato e o logo da sua empresa
        </p>

        {error && (
          <div style={{
            backgroundColor: '#ffebee',
            padding: '12px',
            borderRadius: '4px',
            color: '#c62828',
            marginBottom: '15px',
            border: '1px solid #ffcdd2'
          }}>
            ❌ {error}
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: '#e8f5e9',
            padding: '12px',
            borderRadius: '4px',
            color: '#2e7d32',
            marginBottom: '15px',
            border: '1px solid #c8e6c9'
          }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Seção: Logo */}
          <div style={{
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: isDark ? '#1a1a1a' : '#f8f9fa',
            borderRadius: '8px',
            border: `1px dashed ${colors.border}`
          }}>
            <h3 style={{ color: colors.text, marginTop: 0 }}>🖼️ Logo da Empresa</h3>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '30px',
              flexWrap: 'wrap'
            }}>
              {/* Preview do Logo */}
              <div style={{
                width: '120px',
                height: '120px',
                border: `2px solid ${colors.border}`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
                overflow: 'hidden'
              }}>
                {logoPreview ? (
                  <img 
                    src={logoPreview} 
                    alt="Preview" 
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Logo empresaId={empresaId} size="large" showName={false} />
                )}
              </div>

              <div>
                <input
                  id="logoInput"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('logoInput').click()}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '10px'
                  }}
                >
                  <FaUpload /> Escolher Logo
                </button>
                {logoFile && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FaTrash /> Remover
                  </button>
                )}
                {logoFile && (
                  <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                    Arquivo: {logoFile.name} ({(logoFile.size / 1024).toFixed(0)} KB)
                  </p>
                )}
                <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                  Formatos: JPG, PNG, GIF, WEBP | Máx: 5MB
                </p>
              </div>
            </div>
          </div>

          {/* Seção: Dados da Empresa */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Nome Empresa - READONLY */}
            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Razão Social <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                name="nome_empresa"
                value={formData.nome_empresa}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Nome Fantasia */}
            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Nome Fantasia
              </label>
              <input
                name="nome_fantasia"
                value={formData.nome_fantasia}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Seção: Contato */}
          <h3 style={{ color: colors.text, margin: '25px 0 15px 0' }}>📞 Contato</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                <FaPhone style={{ marginRight: '5px' }} /> Telefone
              </label>
              <input
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                placeholder="(11) 99999-9999"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                <FaEnvelope style={{ marginRight: '5px' }} /> Email
              </label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="contato@empresa.com"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                <FaGlobe style={{ marginRight: '5px' }} /> Site
              </label>
              <input
                name="site"
                value={formData.site}
                onChange={handleChange}
                placeholder="www.empresa.com"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Seção: Endereço */}
          <h3 style={{ color: colors.text, margin: '25px 0 15px 0' }}>
            <FaMapMarkerAlt style={{ marginRight: '5px' }} /> Endereço
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                CEP
              </label>
              <input
                name="cep"
                value={formData.cep}
                onChange={handleChange}
                placeholder="00000-000"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Logradouro
              </label>
              <input
                name="logradouro"
                value={formData.logradouro}
                onChange={handleChange}
                placeholder="Rua, Avenida..."
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Número
              </label>
              <input
                name="numero"
                value={formData.numero}
                onChange={handleChange}
                placeholder="123"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Complemento
              </label>
              <input
                name="complemento"
                value={formData.complemento}
                onChange={handleChange}
                placeholder="Sala, Bloco..."
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Bairro
              </label>
              <input
                name="bairro"
                value={formData.bairro}
                onChange={handleChange}
                placeholder="Centro"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Cidade
              </label>
              <input
                name="cidade"
                value={formData.cidade}
                onChange={handleChange}
                placeholder="São Paulo"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Estado
              </label>
              <select
                name="estado"
                value={formData.estado}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">Selecione</option>
                <option value="AC">Acre</option>
                <option value="AL">Alagoas</option>
                <option value="AP">Amapá</option>
                <option value="AM">Amazonas</option>
                <option value="BA">Bahia</option>
                <option value="CE">Ceará</option>
                <option value="DF">Distrito Federal</option>
                <option value="ES">Espírito Santo</option>
                <option value="GO">Goiás</option>
                <option value="MA">Maranhão</option>
                <option value="MT">Mato Grosso</option>
                <option value="MS">Mato Grosso do Sul</option>
                <option value="MG">Minas Gerais</option>
                <option value="PA">Pará</option>
                <option value="PB">Paraíba</option>
                <option value="PR">Paraná</option>
                <option value="PE">Pernambuco</option>
                <option value="PI">Piauí</option>
                <option value="RJ">Rio de Janeiro</option>
                <option value="RN">Rio Grande do Norte</option>
                <option value="RS">Rio Grande do Sul</option>
                <option value="RO">Rondônia</option>
                <option value="RR">Roraima</option>
                <option value="SC">Santa Catarina</option>
                <option value="SP">São Paulo</option>
                <option value="SE">Sergipe</option>
                <option value="TO">Tocantins</option>
              </select>
            </div>

            <div>
              <label style={{ color: colors.label, display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                País
              </label>
              <input
                name="pais"
                value={formData.pais}
                onChange={handleChange}
                placeholder="BRASIL"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: colors.inputBg,
                  color: colors.inputText,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Botão Salvar */}
          <div style={{ marginTop: '30px', display: 'flex', gap: '15px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '12px 40px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                opacity: saving ? 0.7 : 1
              }}
            >
              <FaSave /> {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConfigurarEmpresa;